import React, { FC, useEffect, useRef, useState } from "react";
import { Accordion } from "react-bootstrap";
import {
  convertDateToLocalIsoDate,
  getLabelName,
  getLookUpItemNumericValue,
  getUserId,
} from "../../utils";
import * as yup from "yup";

import { useFormik } from "formik";
import {
  ActionTypeEnum,
  AcTransactionModel,
  AcTransactionTypeEnum,
  HasFormIdModel,
  LookupItemModel,
  RequestActionModel,
  ResponseBaseModel,
  RowStateEnum,
  TransactionStatusEnum,
} from "../../models";
import { SaveAcTransaction } from "../../serviceBroker/acTransactionsApiServiceBroker";
import {
  ButtonBox,
  InputDatePicker,
  LabelBox,
  PrivilegesChecker,
  SelectBox,
  TextBox,
  UploadDialog,
} from "..";
import {
  getAccountBalance,
  getPageNameBasedOnTransactionType,
} from "./businessLogic/acTransactionBl";
import { UploadedFileModel } from "../../models/files/fileModel";
import { useNavigate, useSearchParams } from "react-router-dom";

interface AcTransactionFormProps extends HasFormIdModel {
  acTransactionType: AcTransactionTypeEnum;
  request: AcTransactionModel;
  setLoading: Function;
  accountList: LookupItemModel[];
  inOutAccountList: LookupItemModel[];
  emloyeeList: LookupItemModel[];
  currencyList: LookupItemModel[];
  coastCenterList: LookupItemModel[];
  onActionEvent: (o: RequestActionModel) => void;
}

export const AcTransactionForm: FC<AcTransactionFormProps> = ({
  acTransactionType,
  request,
  setLoading,
  accountList,
  inOutAccountList,
  currencyList,
  emloyeeList,
  coastCenterList,
  onActionEvent = () => {},
  formId,
}) => {
  //#region variables
  const TransactionTypes = {
    fromAccountTypes: [
      AcTransactionTypeEnum.MoneyIn,
      AcTransactionTypeEnum.CustomerPayment,
      AcTransactionTypeEnum.RefundSupplierPayments,
    ],

    toAccountTypes: [
      AcTransactionTypeEnum.MoneyOut,
      AcTransactionTypeEnum.RefundCustomerPayments,
      AcTransactionTypeEnum.SupplierPayments,
    ],
  };
  type FormFields = keyof Pick<
    AcTransactionModel,
    | "FromAccount_ID"
    | "ToAccount_ID"
    | "CostCenter_ID"
    | "Currency_ID"
    | "Employee_Id"
    | "Value"
  >;
  //#endregion
  //#region state
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectBoxRefs = {
    currency: useRef<any>(),
    costCenter: useRef<any>(),
    fromAccount: useRef<any>(),
    toAccount: useRef<any>(),
  };

  const [pageName, setPageName] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileModel[]>([]);

  const validationSchema = yup.object({
    FromAccount_ID: yup.string().required(getLabelName("required")),
    ToAccount_ID: yup.string().required(getLabelName("required")),
  });
  //#endregion
  //#region functions
  const clearSelectBoxRefs = (refs: any[]) => {
    refs.forEach((ref) => {
      if (ref.current) {
        ref.current.clearValue();
      }
    });
  };
  const validateRequiredField = (
    value: any,
    fieldName: string
  ): string | null => {
    if (value === null || value === undefined || value === 0) {
      return getLabelName("required");
    }
    return null;
  };
  const getDefaultValues = (lists: {
    currencyList: LookupItemModel[];
    coastCenterList: LookupItemModel[];
    accountList: LookupItemModel[];
    inOutAccountList: LookupItemModel[];
  }) => ({
    Currency_ID: lists.currencyList?.[0]?.value
      ? Number(lists.currencyList[0].value)
      : 0,
    CostCenter_ID: lists.coastCenterList?.[0]?.value
      ? Number(lists.coastCenterList[0].value)
      : 0,
    accountId: lists.accountList?.[0]?.value
      ? Number(lists.accountList[0].value)
      : 0,
    inOutAccountId: lists.inOutAccountList?.[0]?.value
      ? Number(lists.inOutAccountList[0].value)
      : 0,
  });
  const getAccountIdByTransactionType = (
    formik: any,
    acTransactionType: AcTransactionTypeEnum
  ) => {
    if (TransactionTypes.fromAccountTypes.includes(acTransactionType)) {
      return formik.values.FromAccount_ID;
    }
    if (TransactionTypes.toAccountTypes.includes(acTransactionType)) {
      return formik.values.ToAccount_ID;
    }
    return 0;
  };
  const initializeValues = async (
    acTransactionRequest: AcTransactionModel
  ): Promise<AcTransactionModel> => {
    if (!acTransactionRequest.isNewRequest) return acTransactionRequest;
    const defaultValues = getDefaultValues({
      currencyList,
      coastCenterList,
      accountList,
      inOutAccountList,
    });

    const updatedRequest = { ...acTransactionRequest };

    if (TransactionTypes.fromAccountTypes.includes(acTransactionType)) {
      updatedRequest.FromAccount_ID =
        acTransactionRequest.TransactionDueDate_ID != null &&
        acTransactionRequest.TransactionDueDate_ID != 0
          ? acTransactionRequest.FromAccount_ID
          : defaultValues.accountId;
      updatedRequest.ToAccount_ID = defaultValues.inOutAccountId;
    }
    if (TransactionTypes.toAccountTypes.includes(acTransactionType)) {
      updatedRequest.FromAccount_ID = defaultValues.inOutAccountId;
      updatedRequest.ToAccount_ID =
        acTransactionRequest.TransactionDueDate_ID != null &&
        acTransactionRequest.TransactionDueDate_ID != 0
          ? acTransactionRequest.ToAccount_ID
          : defaultValues.inOutAccountId;
    }

    updatedRequest.Currency_ID = defaultValues.Currency_ID;
    updatedRequest.CostCenter_ID =
      acTransactionRequest.TransactionDueDate_ID != null &&
      acTransactionRequest.TransactionDueDate_ID != 0
        ? acTransactionRequest.CostCenter_ID
        : defaultValues.CostCenter_ID;
    updatedRequest.fromAccountBalance = await getAccountBalance(
      updatedRequest.FromAccount_ID,
      updatedRequest.Currency_ID
    );
    updatedRequest.toAccountBalance = await getAccountBalance(
      updatedRequest.ToAccount_ID,
      updatedRequest.Currency_ID
    );
    //updatedRequest.balance = await getAccountBalance(defaultValues.accountId,updatedRequest.Currency_ID);
    return updatedRequest;
  };
  const handleSubmit = async (
    request: AcTransactionModel
  ): Promise<ResponseBaseModel<AcTransactionModel>> => {
    const response: ResponseBaseModel<AcTransactionModel> = {
      Result: undefined,
      Errors: [],
      status: Number(TransactionStatusEnum.Failed),
    };

    try {
      setLoading(true);
      const updatedRequest = {
        ...request,
        rowState:
          request.ID == 0
            ? Number(RowStateEnum.Add)
            : request.rowState == 0
            ? RowStateEnum.Update
            : request.rowState,
        CreatedBy: request.ID == 0 ? getUserId() : request.CreatedBy,
        ModifiedBy: request.ID == 0 ? getUserId() : request.ModifiedBy,
        Date:
          request.ID == 0
            ? convertDateToLocalIsoDate(request.Date ?? new Date()) ??
              new Date()
            : request.Date,
        CreationDate:
          request.ID == 0
            ? convertDateToLocalIsoDate(request.CreationDate ?? new Date()) ??
              new Date()
            : request.CreationDate,
        ModificationDate:
          request.ID == 0
            ? null
            : convertDateToLocalIsoDate(
                request.ModificationDate ?? new Date()
              ) ?? new Date(),
        UploadedDocuments: uploadedFiles
          .filter((x) => x.rowState)
          .map((item) => {
            const { File64Bit, ...rest } = item;
            return rest;
          }),
      };

      const apiResponse = await SaveAcTransaction(updatedRequest);
      response.Result = apiResponse.Result;
      response.Errors = apiResponse.Errors;
      response.status = !apiResponse.Errors?.length
        ? Number(TransactionStatusEnum.Success)
        : Number(TransactionStatusEnum.Failed);
    } catch (err: any) {
      response.Errors = [{ MessageAr: err, MessageEn: err }];
    } finally {
      setLoading(false);
    }
    return response;
  };
  const urlHasParameters = (): boolean => searchParams.toString() !== "";
  const reloadWithoutParams = () => {
    navigate(location.pathname, { replace: true });
  };
  const handleResetFormWithUrls = async () => {
    await handleResetForm();
    if (urlHasParameters()) {
      reloadWithoutParams();
    }
  };
  const handleResetForm = async () => {
    clearSelectBoxRefs(Object.values(selectBoxRefs));
    const newValues = await initializeValues(request);
    setUploadedFiles([]);
    formik.resetForm({ values: newValues });
  };
  //#endregion
  //#region formik
  const formik = useFormik({
    initialValues: request,
    validationSchema,
    validate: (values: AcTransactionModel) => {
      const errors: Partial<Record<FormFields, string>> = {};
      const requiredFields: FormFields[] = [
        "FromAccount_ID",
        "ToAccount_ID",
        "CostCenter_ID",
        "Currency_ID",
        "Value",
      ];

      requiredFields.forEach((field) => {
        const error = validateRequiredField(values[field], field);
        if (error) errors[field] = error;
      });
      return errors;
    },
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const response = await handleSubmit(values);
      const isSuccess =
        response.status === Number(TransactionStatusEnum.Success);
      onActionEvent({
        request: response,
        action: isSuccess ? ActionTypeEnum.Success : ActionTypeEnum.Failed,
      });

      if (isSuccess) {
        await handleResetForm();
      }
    },
  });
  //#endregion
  //#region useEffect
  useEffect(() => {
    const initializeForm = async () => {
      setLoading(true);
      try {
        await formik.setValues(await initializeValues(request));
      } finally {
        setLoading(false);
      }
    };
    initializeForm().then(() => {});
  }, [request]);
  useEffect(() => {
    setPageName(
      getLabelName(getPageNameBasedOnTransactionType(acTransactionType))
    );
  }, [acTransactionType]);
  //#endregion
  //#region html
  return (
    <form onSubmit={formik.handleSubmit}>
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>{pageName}</Accordion.Header>
          <Accordion.Body>
            {/* <div className="row row-cols-1 row-cols-xxl-3 row-cols-xl-3 row-cols-lg-3 row-cols-md-2 row-cols-sm-1 g-md-4 g-sm-4 mb-4">
              <LabelBox
                id="codeLabel"
                labelName={getLabelName("Code")}
                inputValue={formik.values.ID}
              />

              <SelectBox
                id="fromAccountSelectBox"
                labelName={getLabelName("From_Account")}
                isSingleSelect={true}
                selectedValues={
                  formik.values.FromAccount_ID
                    ? [formik.values.FromAccount_ID.toString()]
                    : []
                }
                source={
                  TransactionTypes.fromAccountTypes.includes(acTransactionType)
                    ? accountList
                    : inOutAccountList
                }
                onStatusChange={async (e: LookupItemModel) => {
                  const value = getLookUpItemNumericValue(e);
                  const balance =
                    value !== 0
                      ? await getAccountBalance(
                          value ?? 0,
                          formik.values.Currency_ID
                        )
                      : 0;
                  console.log("res_x_fromAccountBalance", balance);
                  await formik.setFieldValue("FromAccount_ID", value);
                  await formik.setFieldValue("fromAccountBalance", balance);
                }}
                multiselectRef={selectBoxRefs.fromAccount}
                errorText={formik.errors.FromAccount_ID}
              />
              <LabelBox
                labelName={getLabelName("Balance")}
                inputValue={formik.values.fromAccountBalance}
              />
              <SelectBox
                labelName="Employee"
                isSingleSelect={true}
                selectedValues={
                  formik.values.Employee_Id
                    ? [formik.values.Employee_Id.toString()]
                    : []
                }
                source={emloyeeList}
                onStatusChange={async (e: LookupItemModel) => {
                  const value = getLookUpItemNumericValue(e);
                  await formik.setFieldValue("Employee_Id", value);
                }}
              />
              <SelectBox
                id="toAccountSelectBox"
                labelName={getLabelName("To Account")}
                isSingleSelect={true}
                selectedValues={
                  formik.values.ToAccount_ID
                    ? [formik.values.ToAccount_ID.toString()]
                    : []
                }
                source={
                  TransactionTypes.toAccountTypes.includes(acTransactionType)
                    ? accountList
                    : inOutAccountList
                }
                onStatusChange={async (e: LookupItemModel) => {
                  const value = getLookUpItemNumericValue(e);
                  const balance =
                    value !== 0
                      ? await getAccountBalance(
                          value ?? 0,
                          formik.values.Currency_ID
                        )
                      : 0;
                  await formik.setFieldValue("ToAccount_ID", value);
                  await formik.setFieldValue("toAccountBalance", balance);
                }}
                multiselectRef={selectBoxRefs.toAccount}
                errorText={formik.errors.ToAccount_ID}
              />
              <LabelBox
                labelName={getLabelName("Balance")}
                inputValue={formik.values.toAccountBalance}
              />

              {currencyList.length > 1 && (
                <SelectBox
                  id="currencySelectBox"
                  labelName={getLabelName("Currency")}
                  isSingleSelect={true}
                  selectedValues={
                    formik.values.Currency_ID
                      ? [formik.values.Currency_ID.toString()]
                      : []
                  }
                  source={currencyList}
                  onStatusChange={async (e: LookupItemModel) => {
                    const value = getLookUpItemNumericValue(e);
                    const accountId = getAccountIdByTransactionType(
                      formik,
                      acTransactionType
                    );
                    const balance =
                      value !== 0 && accountId !== 0
                        ? await getAccountBalance(accountId, value ?? 0)
                        : 0;
                    await formik.setFieldValue("Currency_ID", value);
                    await formik.setFieldValue("balance", balance);
                  }}
                  multiselectRef={selectBoxRefs.currency}
                  errorText={formik.errors.Currency_ID}
                />
              )}

              <SelectBox
                id="costCenterSelectBox"
                labelName={getLabelName("cmbxCostCenter")}
                isSingleSelect={true}
                selectedValues={
                  formik.values.CostCenter_ID
                    ? [formik.values.CostCenter_ID.toString()]
                    : []
                }
                source={coastCenterList}
                onStatusChange={async (e: LookupItemModel) => {
                  const value = getLookUpItemNumericValue(e);
                  await formik.setFieldValue("CostCenter_ID", value);
                }}
                multiselectRef={selectBoxRefs.costCenter}
                errorText={formik.errors.CostCenter_ID}
              />

              <TextBox
                inputName="Value"
                labelName="Value"
                type="price"
                preventNegative={true}
                errorText={formik.errors.Value}
                inputValue={formik.values.Value}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />

              <InputDatePicker
                id="datePicker"
                selectedDate={new Date(formik.values.Date.toString())}
                className="form-control"
                InputLabel="Date"
                name="Date"
                onChange={(date: Date) => {
                  formik.setFieldValue("Date", date);
                }}
                maxDate={new Date()}
              />
            </div> */}
            {/* <div className="row row-cols-1 row-cols-xxl-12 row-cols-xl-12 row-cols-lg-12 row-cols-md-12 row-cols-sm-12 g-md-12 g-sm-12">
              <div className="col-xxl-8 col-xl-8 col-lg-8 col-md-8 col-sm-12">
                <TextBox
                  inputName="Note"
                  labelName="Note"
                  errorText={formik.errors.Note}
                  type="textarea"
                  inputValue={formik.values.Note}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
              <UploadDialog
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
              />
            </div>

            <div className="row mt-3">
              <div className="col-xl-12col-lg-12col-md-12col-sm-12col-12d-flex justify-content-end">
                <PrivilegesChecker
                    formId={formId}
                    action={request.ID ? "EnableUpdate" : "EnableSave"}
                >
                  <ButtonBox
                      type="submit"
                      iconType="content-save"
                      className="btn-gradient-primary mx-3 btn-fw"
                  >
                    {getLabelName(request.ID ? "Update" : "Save")}
                  </ButtonBox>
                </PrivilegesChecker>
                <ButtonBox
                    variant="danger"
                    type="button"
                    iconType="receipt"
                    className="btn-fw"
                    onClick={async () => {
                      onActionEvent({ action: ActionTypeEnum.Clear });
                      await handleResetForm();
                    }}
                >
                  {getLabelName("New")}
                </ButtonBox>
              </div>
            </div> */}
            <div className="row g-md-4 g-sm-4 mb-4">
              <div className="col-12 col-md-6 col-lg-4">
                <LabelBox
                  id="codeLabel"
                  labelName={getLabelName("Code")}
                  inputValue={formik.values.ID}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <SelectBox
                  id="fromAccountSelectBox"
                  labelName={getLabelName("From_Account")}
                  isSingleSelect={true}
                  selectedValues={
                    formik.values.FromAccount_ID
                      ? [formik.values.FromAccount_ID.toString()]
                      : []
                  }
                  source={
                    TransactionTypes.fromAccountTypes.includes(
                      acTransactionType
                    )
                      ? accountList
                      : inOutAccountList
                  }
                  onStatusChange={async (e: LookupItemModel) => {
                    const value = getLookUpItemNumericValue(e);
                    const balance =
                      value !== 0
                        ? await getAccountBalance(
                            value ?? 0,
                            formik.values.Currency_ID
                          )
                        : 0;
                    console.log("res_x_fromAccountBalance", balance);
                    await formik.setFieldValue("FromAccount_ID", value);
                    await formik.setFieldValue("fromAccountBalance", balance);
                  }}
                  multiselectRef={selectBoxRefs.fromAccount}
                  errorText={formik.errors.FromAccount_ID}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <LabelBox
                  labelName={getLabelName("Balance")}
                  inputValue={formik.values.fromAccountBalance}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <SelectBox
                  labelName="Employee"
                  isSingleSelect={true}
                  selectedValues={
                    formik.values.Employee_Id
                      ? [formik.values.Employee_Id.toString()]
                      : []
                  }
                  source={emloyeeList}
                  onStatusChange={async (e: LookupItemModel) => {
                    const value = getLookUpItemNumericValue(e);
                    await formik.setFieldValue("Employee_Id", value);
                  }}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <SelectBox
                  id="toAccountSelectBox"
                  labelName={getLabelName("To Account")}
                  isSingleSelect={true}
                  selectedValues={
                    formik.values.ToAccount_ID
                      ? [formik.values.ToAccount_ID.toString()]
                      : []
                  }
                  source={
                    TransactionTypes.toAccountTypes.includes(acTransactionType)
                      ? accountList
                      : inOutAccountList
                  }
                  onStatusChange={async (e: LookupItemModel) => {
                    const value = getLookUpItemNumericValue(e);
                    const balance =
                      value !== 0
                        ? await getAccountBalance(
                            value ?? 0,
                            formik.values.Currency_ID
                          )
                        : 0;
                    await formik.setFieldValue("ToAccount_ID", value);
                    await formik.setFieldValue("toAccountBalance", balance);
                  }}
                  multiselectRef={selectBoxRefs.toAccount}
                  errorText={formik.errors.ToAccount_ID}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <LabelBox
                  labelName={getLabelName("Balance")}
                  inputValue={formik.values.toAccountBalance}
                />
              </div>
              {currencyList.length > 1 && (
                <div className="col-12 col-md-6 col-lg-4">
                  <SelectBox
                    id="currencySelectBox"
                    labelName={getLabelName("Currency")}
                    isSingleSelect={true}
                    selectedValues={
                      formik.values.Currency_ID
                        ? [formik.values.Currency_ID.toString()]
                        : []
                    }
                    source={currencyList}
                    onStatusChange={async (e: LookupItemModel) => {
                      const value = getLookUpItemNumericValue(e);
                      const accountId = getAccountIdByTransactionType(
                        formik,
                        acTransactionType
                      );
                      const balance =
                        value !== 0 && accountId !== 0
                          ? await getAccountBalance(accountId, value ?? 0)
                          : 0;
                      await formik.setFieldValue("Currency_ID", value);
                      await formik.setFieldValue("balance", balance);
                    }}
                    multiselectRef={selectBoxRefs.currency}
                    errorText={formik.errors.Currency_ID}
                  />
                </div>
              )}
              <div className="col-12 col-md-6 col-lg-4">
                <SelectBox
                  id="costCenterSelectBox"
                  labelName={getLabelName("cmbxCostCenter")}
                  isSingleSelect={true}
                  selectedValues={
                    formik.values.CostCenter_ID
                      ? [formik.values.CostCenter_ID.toString()]
                      : []
                  }
                  source={coastCenterList}
                  onStatusChange={async (e: LookupItemModel) => {
                    const value = getLookUpItemNumericValue(e);
                    await formik.setFieldValue("CostCenter_ID", value);
                  }}
                  multiselectRef={selectBoxRefs.costCenter}
                  errorText={formik.errors.CostCenter_ID}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <TextBox
                  inputName="Value"
                  labelName="Value"
                  type="price"
                  preventNegative={true}
                  errorText={formik.errors.Value}
                  inputValue={formik.values.Value}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <InputDatePicker
                  id="datePicker"
                  selectedDate={new Date(formik.values.Date.toString())}
                  className="form-control"
                  InputLabel="Date"
                  name="Date"
                  onChange={(date: Date) => {
                    formik.setFieldValue("Date", date);
                  }}
                  maxDate={new Date()}
                />
              </div>
            </div>
            <div className="row g-3 w-100">
              <div className="col w-100">
                <TextBox
                  inputName="Note"
                  labelName="Note"
                  errorText={formik.errors.Note}
                  type="textarea"
                  inputValue={formik.values.Note}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <UploadDialog
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  requestId={request.ID}
                  entityDocumentType="AcTrasnaction"
                />
              </div>
            </div>
            <div className="row mt-3 w-100">
              <div className="col-12 d-flex justify-content-end">
                <PrivilegesChecker
                  formId={formId}
                  action={request.ID ? "EnableUpdate" : "EnableSave"}
                >
                  <ButtonBox
                    type="submit"
                    iconType="content-save"
                    className="btn-gradient-primary mx-3 btn-fw"
                  >
                    {getLabelName(request.ID ? "Update" : "Save")}
                  </ButtonBox>
                </PrivilegesChecker>
                <ButtonBox
                  variant="danger"
                  type="button"
                  iconType="receipt"
                  className="btn-fw"
                  onClick={async () => {
                    onActionEvent({ action: ActionTypeEnum.Clear });
                    await handleResetForm();
                  }}
                >
                  {getLabelName("New")}
                </ButtonBox>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </form>
  );
  //#endregion
};
